import { useRef, useState } from 'react';
import FranchiseBtn from '../../assets/images/SFC-li-img.png';
import OutletBtn from '../../assets/images/location-pin.png';
import { LocationMapItem } from '../../types/place.types';
import { percentCoordsFromClick } from './mapCoordinates';
import './MapPinLayer.css';

interface GroupPin {
  groupId: number;
  title: string;
  map_x: number;
  map_y: number;
  locations: LocationMapItem[];
}

interface MapPinEditableConfig {
  map_x: number | null;
  map_y: number | null;
  map_pin_type?: 'franchise' | 'outlet';
  onPlace: (map_x: number, map_y: number) => void;
}

interface MapPinLayerProps {
  mapImage: string;
  locations: LocationMapItem[];
  onSoloClick: (location: LocationMapItem) => void;
  onGroupItemClick: (location: LocationMapItem) => void;
  editable?: MapPinEditableConfig;
}

export const computeGroupPins = (locations: LocationMapItem[]): GroupPin[] => {
  const groupMap = locations.reduce((acc: Record<number, LocationMapItem[]>, loc) => {
    if (!loc.map_group_id) return acc;
    if (!acc[loc.map_group_id]) acc[loc.map_group_id] = [];
    acc[loc.map_group_id].push(loc);
    return acc;
  }, {});

  return (Object.entries(groupMap) as [string, LocationMapItem[]][]).map(([groupId, locs]) => ({
    groupId: Number(groupId),
    title: locs[0].location_map_group?.title ?? 'Group',
    map_x: locs.reduce((sum, l) => sum + l.map_x, 0) / locs.length,
    map_y: locs.reduce((sum, l) => sum + l.map_y, 0) / locs.length,
    locations: locs,
  }));
};

export const MapPinLayer: React.FC<MapPinLayerProps> = ({
  mapImage,
  locations,
  onSoloClick,
  onGroupItemClick,
  editable,
}) => {
  const [activeGroup, setActiveGroup] = useState<number | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const soloLocations = locations.filter((l) => !l.map_group_id);
  const groupPins = computeGroupPins(locations);
  const isEditable = Boolean(editable);

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!editable?.onPlace || !imageRef.current) return;
    if ((e.target as HTMLElement).closest('.map-button-wrapper, .map-group-wrapper, .map-group-dropdown')) {
      return;
    }

    const { x, y } = percentCoordsFromClick(imageRef.current, e.clientX, e.clientY);
    editable.onPlace(x, y);
  };

  const handleGroupClick = (groupId: number) => {
    if (isEditable) return;
    setActiveGroup(activeGroup === groupId ? null : groupId);
  };

  const contextClass = isEditable ? ' map-button-wrapper--context' : '';
  const groupContextClass = isEditable ? ' map-group-wrapper--context' : '';

  return (
    <div
      className={`map-container${isEditable ? ' map-container--editable' : ''}`}
      onClick={(e) => {
        if (isEditable) {
          handleMapClick(e);
          return;
        }
        if ((e.target as HTMLElement).closest('.map-group-dropdown')) return;
        setActiveGroup(null);
      }}
    >
      <img ref={imageRef} className="w-100 d-block" src={mapImage} alt="Nationwide map" />

      {soloLocations.map((location) => (
        <div
          key={location.id}
          className={`map-button-wrapper${contextClass}`}
          style={{ left: `${location.map_x}%`, top: `${location.map_y}%` }}
        >
          <div
            className={location.map_pin_type === 'franchise' ? 'map-button' : 'map-button-outlet'}
            style={{
              backgroundImage: `url(${location.map_pin_type === 'franchise' ? FranchiseBtn : OutletBtn})`,
            }}
            onClick={() => !isEditable && onSoloClick(location)}
          />
          {!isEditable && <span className="map-button-label">{location.title}</span>}
        </div>
      ))}

      {groupPins.map((group) => (
        <div
          key={group.groupId}
          className={`map-group-wrapper${groupContextClass}`}
          style={{ left: `${group.map_x}%`, top: `${group.map_y}%` }}
        >
          <div
            title={group.title}
            className="map-group-pin"
            onClick={(e) => {
              e.stopPropagation();
              handleGroupClick(group.groupId);
            }}
          >
            <img src={FranchiseBtn} alt="" style={{ opacity: 0.45 }} />
            <img src={FranchiseBtn} alt="" />
          </div>

          {!isEditable && activeGroup === group.groupId && (
            <div className="map-group-dropdown">
              <p className="map-group-dropdown-header">{group.title}</p>
              {group.locations.map((loc) => (
                <div
                  key={loc.id}
                  className="map-group-dropdown-item"
                  onClick={() => onGroupItemClick(loc)}
                >
                  {loc.title}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {editable && editable.map_x != null && editable.map_y != null && (
        <div
          className="map-button-wrapper map-button-wrapper--editable"
          style={{ left: `${editable.map_x}%`, top: `${editable.map_y}%` }}
        >
          <div
            className={editable.map_pin_type === 'outlet' ? 'map-button-outlet' : 'map-button'}
            style={{
              backgroundImage: `url(${editable.map_pin_type === 'outlet' ? OutletBtn : FranchiseBtn})`,
            }}
          />
        </div>
      )}
    </div>
  );
};
